import React, { useEffect, useContext, useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import { Container } from '../layout/AppLayout.js';
import Spinner from '../shared/Spinner.js';

import useFeatureChecker, { AllowedFeature, DeniedFeature } from '../shared/FeatureChecker.js';
import Header, { HeaderActions } from "../shared/Header.js";
import { useNotifications } from '../shared/Notifications.js';
import { DestroyButton } from '../shared/Buttons.js';
import FileUploaderModal from '../shared/FileUploaderModal.js';
import { BaseModal } from '../shared/Modals.js';
import { useMutationClient } from '../shared/hooks.js';

import { CurrentUserContext } from '../../App.js';
import SensorDetailSubmenu from './SensorDetailSubmenu.js';
import Modal3DViewer from '../shared/Modal3DViewer.js';

const MySwal = withReactContent(Swal);

// Modal personalizado para subir modelos 3D con nombre
function Model3DUploadModal({ onClose, onSuccess, notifications, validateFileExtension }) {
  const [modelName, setModelName] = useState('')
  const [showThumbnailUploader, setShowThumbnailUploader] = useState(false)
  const [showThumbnailConfirmation, setShowThumbnailConfirmation] = useState(false)
  const [showFileUploader, setShowFileUploader] = useState(false)
  const [thumbnailFiles, setThumbnailFiles] = useState(null)
  const [nameError, setNameError] = useState('')

  const handleContinue = () => {
    if (!modelName.trim()) {
      setNameError('El nombre del modelo es obligatorio')
      return
    }
    setNameError('')
    setShowThumbnailUploader(true)
  }

  const handleContinueToModel3D = () => {
    setShowThumbnailConfirmation(false)
    setShowFileUploader(true)
  }

  const handleValidateImageFiles = (files) => {
    // Validar que sean imágenes
    try {
      const invalidFiles = files.filter(file => {
        if (!file || !file.name) {
          return true
        }
        const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
        return !['.jpg', '.jpeg', '.png', '.gif', '.webp'].includes(extension)
      })

      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map(f => f?.name || 'archivo sin nombre').join(', ')
        notifications.danger(
          `Los siguientes archivos no tienen un formato válido: ${fileNames}. Solo se permiten imágenes (JPG, PNG, GIF, WEBP).`
        )
        return false
      }

      return true
    } catch (error) {
      console.error('Error en validación de archivos:', error)
      notifications.danger('Error al validar los archivos')
      return false
    }
  }

  const handleValidate3DFiles = (files) => {
    // Validar INMEDIATAMENTE cuando se seleccionan - ANTES de solicitar URLs de AWS
    try {
      const invalidFiles = files.filter(file => {
        if (!file || !file.name) {
          return true // Considerar inválido si no tiene nombre
        }
        return !validateFileExtension(file.name)
      })

      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map(f => f?.name || 'archivo sin nombre').join(', ')
        notifications.danger(
          `Los siguientes archivos no tienen un formato válido: ${fileNames}. Solo se permiten archivos GLB, GLTF, OBJ y FBX.`
        )
        return false // Rechazar archivos
      }

      return true // Aceptar archivos
    } catch (error) {
      console.error('Error en validación de archivos:', error)
      notifications.danger('Error al validar los archivos')
      return false
    }
  }

  const handleThumbnailUploadSuccess = (files) => {
    // Guardar los archivos del thumbnail y mostrar confirmación
    setThumbnailFiles(files)
    setShowThumbnailUploader(false)
    setShowThumbnailConfirmation(true)
  }

  const handleFileUploadSuccess = (files) => {
    // Los archivos ya fueron validados, proceder directo a la API con thumbnail y modelo 3D
    onSuccess(modelName, thumbnailFiles, files)
  }

  if (showFileUploader) {
    return (
      <FileUploaderModal
        title={`Subir Modelo 3D: ${modelName}`}
        description="Sube archivos de modelos 3D (solo GLB, GLTF, OBJ, FBX)"
        onClose={onClose}
        onSuccess={handleFileUploadSuccess}
        onValidateFiles={handleValidate3DFiles}
      />
    )
  }

  if (showThumbnailUploader) {
    return (
      <FileUploaderModal
        title={`Subir Thumbnail: ${modelName}`}
        description="Sube una imagen para el thumbnail (JPG, PNG, GIF, WEBP)"
        onClose={onClose}
        onSuccess={handleThumbnailUploadSuccess}
        onValidateFiles={handleValidateImageFiles}
      />
    )
  }

  if (showThumbnailConfirmation) {
    return (
      <BaseModal
        title="Imagen Subida"
        size="md"
        onClose={onClose}
      >
        <BaseModal.Body>
          <div className="text-center py-3">
            <i className="fas fa-check-circle text-success" style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3">Imagen subida correctamente</h5>
            <p className="text-muted">
              {thumbnailFiles && thumbnailFiles[0] && thumbnailFiles[0].name}
            </p>
            <p className="text-muted mt-3">
              Ahora puedes continuar para subir el modelo 3D
            </p>
          </div>
        </BaseModal.Body>
        <BaseModal.Footer>
          <button
            className="btn btn-outline-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleContinueToModel3D}
          >
            Continuar
          </button>
        </BaseModal.Footer>
      </BaseModal>
    )
  }

  return (
    <BaseModal
      title="Subir Modelo 3D"
      size="md"
      onClose={onClose}
    >
      <BaseModal.Body>
        <Form.Group>
          <Form.Label>Nombre del Modelo <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Ej: Sensor principal, Modelo v2, etc."
            value={modelName}
            onChange={(e) => {
              setModelName(e.target.value)
              if (e.target.value.trim()) {
                setNameError('')
              }
            }}
            isInvalid={!!nameError}
            autoFocus
          />
          {nameError && (
            <Form.Control.Feedback type="invalid">
              {nameError}
            </Form.Control.Feedback>
          )}
          <Form.Text className="text-muted">
            Ingresa un nombre descriptivo para identificar este modelo 3D
          </Form.Text>
        </Form.Group>
      </BaseModal.Body>
      <BaseModal.Footer>
        <button
          className="btn btn-outline-secondary"
          onClick={onClose}
        >
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          onClick={handleContinue}
        >
          Continuar
        </button>
      </BaseModal.Footer>
    </BaseModal>
  )
}

export default function SensorModel3DPage() {
  const CheckFeatures = useFeatureChecker()
  const params = useParams()
  const navigate = useNavigate()
  const notifications = useNotifications()
  const currentUser = useContext(CurrentUserContext)
  const mutationClient = useMutationClient()
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [selectedModel, setSelectedModel] = useState(null)

  // Formatos permitidos para modelos 3D
  const ALLOWED_3D_FORMATS = ['.glb', '.gltf', '.obj', '.fbx']

  // Función para validar extensión de archivo
  const validateFileExtension = (fileName) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'))
    return ALLOWED_3D_FORMATS.includes(extension)
  }

  const { isLoading, data } = useQuery(`sensors?SensorId=${params.SensorId}`)
  const currentSensor = data && data.result && data.result.length && data.result[0]

  const models3DQuery = useQuery(`sensors/${params.SensorId}/models3d`, {
    enabled: !!params.SensorId
  })
  const models3D = models3DQuery.data?.result || []

  const uploadModelMutation = useMutation(
    data => mutationClient.post(`sensors/${params.SensorId}/models3d`, data),
    {
      onSuccess: () => {
        notifications.success('Modelo 3D subido correctamente')
        models3DQuery.refetch()
        setShowUploadModal(false)
      },
      onError: (error) => {
        // Manejar todos los tipos de errores de forma segura
        console.error('Error al subir modelo 3D:', error)

        let errorMessage = 'Error al subir el modelo 3D'

        try {
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error?.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error?.message) {
            errorMessage = error.message
          } else if (typeof error === 'string') {
            errorMessage = error
          }
        } catch (e) {
          console.error('Error al procesar mensaje de error:', e)
        }

        notifications.danger(errorMessage)
        setShowUploadModal(false)
      }
    }
  )

  const deleteModelMutation = useMutation(
    modelId => mutationClient.delete(`sensors/${params.SensorId}/models3d/${modelId}`),
    {
      onSuccess: () => {
        notifications.success('Modelo 3D eliminado correctamente')
        models3DQuery.refetch()
      },
      onError: (error) => {
        console.error('Error al eliminar modelo 3D:', error)
        let errorMessage = 'Error al eliminar el modelo 3D'

        try {
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error?.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error?.message) {
            errorMessage = error.message
          }
        } catch (e) {
          console.error('Error al procesar mensaje de error:', e)
        }

        notifications.danger(errorMessage)
      }
    }
  )

  const handleDeleteModel = (model) => {
    MySwal.fire({
      title: `¿Está seguro que desea eliminar el modelo "${model.name}"?`,
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteModelMutation.mutate(model.id)
      }
    })
  }

  useEffect(() => {
    if (!isLoading && !currentSensor) {
      notifications.warning('El sensor solicitado no existe')
      navigate(`/sensors`)
    }
  }, [currentSensor, isLoading])

  return (
    <Container>
      <Row>
        <Header title="Modelo 3D del dispositivo" items={[
          { label: "Listado de dispositivos", to: "/sensors" },
          { label: "Detalle de dispositivo", to: `/sensors/${params.SensorId}` },
          { label: "Modelo 3D" }
        ]}>
          <HeaderActions>
            <CheckFeatures feature="UPDATE_SENSORS">
              <button
                className="btn btn-primary mr-2"
                onClick={() => setShowUploadModal(true)}
              >+ Subir Modelo 3D</button>
            </CheckFeatures>

            <CheckFeatures feature="UPDATE_SENSORS">
              <Link
                className="btn btn-primary mr-2"
                to={{
                  pathname: `/sensors/${params.SensorId}/edit`,
                  state: { sensor: currentSensor }
                }}
              >Editar sensor</Link>
            </CheckFeatures>

            <CheckFeatures feature="DESTROY_SENSORS">
              <div className="d-inline">
                <DestroyButton
                  label="Eliminar dispositivo"
                  confirmMessage="¿Está seguro que desea eliminar el dispositivo?"
                  successMessage="Dispositivo eliminado correctamente"
                  deletePath={`sensors/${params.SensorId}`}
                  invalidate={`sensors`}
                  redirect="/sensors"
                />
              </div>
            </CheckFeatures>
          </HeaderActions>
        </Header>
      </Row>

      {
        showUploadModal ?
          <Model3DUploadModal
            onClose={() => setShowUploadModal(false)}
            onSuccess={(name, thumbnailFiles, files) => {
              // Proceder con la subida incluyendo el nombre del modelo, thumbnail y archivos 3D
              const payload = {
                name: name,
                file_key: files.map(x => ({
                  id: x.id,
                  name: x.name,
                  size: x.size,
                  type: x.type,
                  file_key: x.file_key
                }))
              }

              // Agregar thumbnail si existe
              if (thumbnailFiles && thumbnailFiles.length > 0) {
                payload.thumbnail = thumbnailFiles.map(x => ({
                  id: x.id,
                  name: x.name,
                  size: x.size,
                  type: x.type,
                  file_key: x.file_key
                }))[0] // Solo tomar el primer archivo del thumbnail
              }

              uploadModelMutation.mutate(payload)
            }}
            notifications={notifications}
            validateFileExtension={validateFileExtension}
          />
        : null
      }

      {
        selectedModel ?
          <Modal3DViewer
            modelName={selectedModel.name}
            modelUrl={selectedModel.url}
            onClose={() => setSelectedModel(null)}
          />
        : null
      }

      <Row>
        <SensorDetailSubmenu SensorId={params.SensorId} />
      </Row>

      <Row className="mb-2">
        <Col className="col-12">
          <Card className="shadow-sm">
            <Card.Header>
              <div className="d-flex justify-content-between align-items-center">
                <span>Modelos 3D</span>
                {currentSensor && (
                  <span className="text-muted" style={{ fontSize: '0.9em' }}>
                    {currentSensor.name} - {currentSensor.type_label}
                  </span>
                )}
              </div>
            </Card.Header>
            <Card.Body>
              <CheckFeatures feature="DETAIL_SENSOR_3D">
                <AllowedFeature>
                  {
                    isLoading || models3DQuery.isLoading ?
                      <div className="text-center my-4 py-4">
                        <Spinner />
                      </div>
                      :
                      models3D.length > 0 ?
                        <div className="row">
                          {models3D.map((model, index) => (
                            <div key={index} className="col-12 col-md-6 col-lg-4 mb-3">
                              <div className="card">
                                {model.thumbnail_url && (
                                  <img
                                    src={model.thumbnail_url}
                                    className="card-img-top"
                                    alt={model.name}
                                    style={{
                                      height: '200px',
                                      objectFit: 'cover',
                                      backgroundColor: '#f8f9fa'
                                    }}
                                  />
                                )}
                                {!model.thumbnail_url && (
                                  <div
                                    className="card-img-top d-flex align-items-center justify-content-center bg-light"
                                    style={{ height: '200px' }}
                                  >
                                    <i className="fas fa-cube text-muted" style={{ fontSize: '3rem' }}></i>
                                  </div>
                                )}
                                <div className="card-body">
                                  <h6 className="card-title">{model.name}</h6>
                                  <button
                                    onClick={() => setSelectedModel(model)}
                                    className="btn btn-sm btn-primary mr-2"
                                  >
                                    Ver modelo
                                  </button>
                                  <a
                                    href={model.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-secondary mr-2"
                                  >
                                    Descargar
                                  </a>
                                  <CheckFeatures feature="DELETE_SENSOR_3D">
                                    <button
                                      onClick={() => handleDeleteModel(model)}
                                      className="btn btn-sm btn-danger"
                                      disabled={deleteModelMutation.isLoading}
                                    >
                                      Eliminar
                                    </button>
                                  </CheckFeatures>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      :
                        <div className="text-center my-4 py-4">
                          <p className="text-muted">No hay modelos 3D cargados para este dispositivo</p>
                          <p className="text-muted" style={{ fontSize: '0.9em' }}>
                            Haz click en el botón "+ Subir Modelo 3D" para agregar un modelo
                          </p>
                        </div>
                  }
                </AllowedFeature>
                <DeniedFeature>
                  <Navigate to={'/error?error=403'} />
                </DeniedFeature>
              </CheckFeatures>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}
